import React, { useCallback, useState, useMemo } from 'react';
import { Modal } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import DraftModal from '../../templates/DraftModal';
import Button from 'react-bootstrap/Button';
import AddNewTemplateFormik from '../../../forms/AddNewTemplateFormContainer';
import { Dropdown, DropdownBody } from '../../../components/dropdown';
import { mutations, queriesV2 } from '../../../api';
import { useNotifications } from '../../../notifications';
import { SuccessMessages } from '../../../success-messages';
import { paths } from '../../../routing';
import { TemplateType } from '../../../consts/forms-types';
import { ReactComponent as DotsIconSvg } from '../../../shared/icons/dotsRoundIcon.svg';

const DotsIcon = React.forwardRef<HTMLSpanElement, React.HTMLAttributes<HTMLSpanElement>>((props, ref) => (
  <span {...props} ref={ref} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><DotsIconSvg /></span>
));
import { TitleFormattedMessage } from '../../../components/titleFormattedMessage';
import MoveClauseModal from './MoveClauseModal';
import CloneTemplateModal from '../../templates/components/CloneTemplateModal';
import ShareToWorkspacesModal from '../../templates/components/ShareToWorkspacesModal';
import PropdocsModal from '../../../components/modal/PropdocsModal';
import { useDropdownControl } from '../../../contexts/DropdownContext';
import styles from '../../templates/styles.module.scss';

type ClauseControlsProps = {
  status: string;
  editPermission: boolean;
  managePermission?: boolean; // New prop for manage_templates permission
  selectedTemplateData: any;
  teamId: string;
  onSuccess: () => void;
  templateType?: TemplateType;
};

type DropdownActionsType = {
  text: string;
  onClick: () => void;
  isAvailable: boolean;
};

enum TemplateActionStatus {
  draft = 'Save as Draft',
  publish = 'Publish',
  view = 'View',
  edit = 'Edit',
  clone = 'Clone',
  move = 'Move',
  share = 'Share',
  unshare = 'Unshare',
}

export enum TemplateStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

const ClauseControls = ({
  status,
  editPermission,
  managePermission,
  selectedTemplateData,
  teamId,
  onSuccess,
  templateType,
}: ClauseControlsProps) => {
  const { formatMessage } = useIntl();
  const dropdownId = useMemo(
    () => `clause-dropdown-${selectedTemplateData?.id || Math.random()}`,
    [selectedTemplateData?.id]
  );
  const { isOpen, onToggle } = useDropdownControl(dropdownId);
  const [isDraftModalOpen, setDraftModalOpen] = useState(false);
  const [isActivateModalOpen, setActivateModalOpen] = useState(false);
  const [showEditTemplate, setShowEditTemplate] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const navigate = useNavigate();
  const [, { success: notifySuccess, error: notifyError }] = useNotifications();
  const [updateDocumentTemplate] = useMutation(mutations.updateDocumentTemplate as any);
  const isOptionAvailable = editPermission;
  const isManageAvailable = managePermission ?? editPermission; // Use managePermission if provided, fallback for backward compatibility
  const isActive = status === TemplateStatus.ACTIVE;

  const navigateToEditor = useCallback(
    (templateId: string, teamId: string) => {
      const dealUrl = paths.templates.document.replace(':teamId', teamId).replace(':templateId', templateId);
      navigate(dealUrl);
    },
    [navigate]
  );

  const onUpdateDraft = async () => {
    const deactivateMessage = SuccessMessages.deactivateClause;
    const activateMessage = SuccessMessages.activateClause;
    const successMessage = isActive ? deactivateMessage : activateMessage;
    try {
      await updateDocumentTemplate({
        variables: { documentTemplateId: selectedTemplateData.id, hidden: isActive },
        refetchQueries: [
          {
            query: queriesV2.getDocumentTemplates,
            variables: {
              teamId,
              templateType: 'clause',
            },
          },
        ],
      });
      notifySuccess({ message: successMessage });
      setDraftModalOpen(false);
      onSuccess();
    } catch (error) {
      notifyError({ message: error });
      return false;
    }
  };

  // Remove old onTemplateClone callback - now handled by CloneTemplateModal

  const allDropDownActions = [
    {
      text: TemplateActionStatus.view,
      onClick: () => {
        navigateToEditor(selectedTemplateData.id, teamId);
      },
      isAvailable: true,
    },
    {
      text: TemplateActionStatus.move,
      onClick: () => {
        setShowMoveModal(true);
      },
      isAvailable: isManageAvailable,
    },
    {
      text: TemplateActionStatus.clone,
      onClick: () => {
        setShowCloneModal(true);
      },
      isAvailable: isManageAvailable && templateType !== TemplateType.UPLOADED,
    },
    {
      text: selectedTemplateData?.sharedToAffiliatedWorkspaces
        ? TemplateActionStatus.unshare
        : TemplateActionStatus.share,
      onClick: () => {
        setShowShareModal(true);
      },
      isAvailable:
        isManageAvailable && !selectedTemplateData?.sharedFromTeam && !selectedTemplateData?.fromOrganization, // Only show for own team clauses
    },
    {
      text: isActive ? 'Deactivate' : 'Activate',
      onClick: isActive ? () => setDraftModalOpen(true) : () => setActivateModalOpen(true),
      isAvailable: isManageAvailable,
    },
  ];

  const dropDownActions = (actions: DropdownActionsType[]) => {
    return actions
      .filter((action) => action.isAvailable)
      .map((action, index) => (
        <Dropdown.Item as={Button} variant="link" onClick={action.onClick} key={index}>
          {action.text}
        </Dropdown.Item>
      ));
  };

  const templateIsUpdated = () => {
    onSuccess();
    setShowEditTemplate(false);
  };

  return (
    <>
      <Dropdown show={isOpen} onToggle={onToggle}>
        <Dropdown.Toggle id="dropdown-clause-actions" className={styles.dottedDropdown} as={DotsIcon}>
          <FormattedMessage id="clauses.actions" defaultMessage={`Clause Actions`} />
        </Dropdown.Toggle>
        <Dropdown.Menu align="right">
          <DropdownBody>{dropDownActions(allDropDownActions)}</DropdownBody>
        </Dropdown.Menu>
      </Dropdown>
      <DraftModal
        isOpen={isDraftModalOpen}
        onClose={() => setDraftModalOpen(false)}
        onUpdateDraft={onUpdateDraft}
        templateType={templateType}
      />
      <PropdocsModal
        show={isActivateModalOpen}
        setShow={() => setActivateModalOpen(false)}
        size="md"
        title={<FormattedMessage id="title.activate-clause" defaultMessage="Activate Clause" />}
      >
        <>
          <div>
            <p>
              <FormattedMessage
                id="phrase.activate-clause"
                defaultMessage="Activating this clause will make it available for insertion into documents."
              />
            </p>
            <p>
              <FormattedMessage
                id="phrase.activate-clause-visibility"
                defaultMessage="Users will see this clause in their clause list and be able to use it."
              />
            </p>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', padding: '1rem 0 1rem 0' }}>
            <Button variant="link" className="text-secondary" onClick={() => setActivateModalOpen(false)}>
              <TitleFormattedMessage id="cta.cancel" defaultMessage="Cancel" />
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                onUpdateDraft();
                setActivateModalOpen(false);
              }}
            >
              <TitleFormattedMessage id="cta.activate" defaultMessage="Activate" />
            </Button>
          </div>
        </>
      </PropdocsModal>
      <Modal show={showEditTemplate} onHide={() => setShowEditTemplate(false)} size="sm">
        <Modal.Header closeButton closeLabel={formatMessage({ id: 'cta.close', defaultMessage: 'Close' })}>
          <Modal.Title>
            <header>
              <FormattedMessage id="title.edit-clause" defaultMessage="Edit Clause" />
            </header>
          </Modal.Title>
        </Modal.Header>
        <AddNewTemplateFormik
          bodyContainer={Modal.Body}
          actionsContainer={Modal.Footer}
          renderSecondaryAction={() => (
            <Button variant="link" className="text-secondary" onClick={() => setShowEditTemplate(false)}>
              <TitleFormattedMessage id="cta.cancel" defaultMessage="Cancel" />
            </Button>
          )}
          existingTemplateFields={selectedTemplateData}
          templateFormEditMode={true}
          onSuccess={templateIsUpdated}
        />
      </Modal>
      <MoveClauseModal
        isOpen={showMoveModal}
        onClose={() => setShowMoveModal(false)}
        templateId={selectedTemplateData?.id || ''}
        templateName={selectedTemplateData?.name || ''}
        teamId={teamId}
        onSuccess={onSuccess}
      />
      <CloneTemplateModal
        isOpen={showCloneModal}
        onClose={() => setShowCloneModal(false)}
        templateId={selectedTemplateData?.id || ''}
        templateName={selectedTemplateData?.name || ''}
        templateType="clause"
        sourceTeamId={teamId}
        onSuccess={onSuccess}
      />
      <ShareToWorkspacesModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        templateId={selectedTemplateData?.id || ''}
        templateName={selectedTemplateData?.name || ''}
        teamId={teamId}
        isShared={selectedTemplateData?.sharedToAffiliatedWorkspaces || false}
        onSuccess={onSuccess}
        type="clause"
      />
    </>
  );
};

export default ClauseControls;
